import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { axiosInstance } from "@/lib/api";
import { queue } from "@/components/Toast";
import type { CalendarMonthPlan } from "@/types/contentCalendar.type";
import type {
  BriefComment,
  BriefRailInfo,
  BriefReviewRound,
  BriefRevisionEntry,
  ProductionBrief,
  ProductionBriefBody,
} from "@/types/productionBrief.type";
import { reconcilePlan } from "./calendarCache";
import { briefReviewPollMs, planBriefsPollMs, slotBriefPollMs } from "./planPolling";
import { clampWhenHidden } from "@/lib/realtime";
import { livePoll, useCalendarLive } from "./calendarLive";

/**
 * The production-brief surface.
 *
 * One thing to notice by its absence: there is no `useRegenerateBrief`. Once a
 * brief has a body, `useEditBrief` is the only path that changes it — a declined
 * brief is rewritten by hand, never by the model. `useRetryBriefGeneration` is
 * not a counter-example: it only touches FAILED briefs, which have no body yet.
 */

export const planBriefsKey = (planId: string | null) => ["planBriefs", planId];
export const slotBriefKey = (slotId: string | null) => ["slotBrief", slotId];
export const briefRevisionsKey = (briefId: string | null) => ["briefRevisions", briefId];
export const briefReviewKey = (planId: string | null) => ["briefReview", planId];

/** What `useSlotBrief` caches: one post's brief and the thread on it. */
type SlotBriefEntry = { brief: ProductionBrief | null; comments: BriefComment[] };

function errorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    return (err.response?.data as { message?: string })?.message ?? err.message ?? fallback;
  }
  return fallback;
}

interface PlanBriefsResponse {
  planStatus: string;
  briefs: ProductionBrief[];
  comments: BriefComment[];
}

/**
 * A brief's revisions carry the client's decisions (and their reasons), but
 * nothing polls them: they are fetched once. When a polled read shows a brief
 * MOVE — the client declined or accepted, a send went out, an edit landed —
 * refetch its revisions, or the decline banner waits for a page reload.
 * `stamps` is `id:status:version:sentVersion` per brief, joined by "|".
 */
function useRefetchRevisionsOnMove(stamps: string) {
  const qc = useQueryClient();
  const seen = useRef(new Map<string, string>());
  useEffect(() => {
    if (!stamps) return;
    for (const stamp of stamps.split("|")) {
      const id = stamp.slice(0, stamp.indexOf(":"));
      const before = seen.current.get(id);
      seen.current.set(id, stamp);
      if (before !== undefined && before !== stamp) {
        void qc.invalidateQueries({ queryKey: briefRevisionsKey(id) });
      }
    }
  }, [stamps, qc]);
}

function briefStamp(brief: Pick<ProductionBrief, "id" | "status" | "version" | "sentVersion">): string {
  return `${brief.id}:${brief.status}:${brief.version}:${brief.sentVersion ?? ""}`;
}

/** Every brief in the month, for the board and the rail. */
export function usePlanBriefs(planId: string | null, enabled = true) {
  const live = useCalendarLive();
  const query = useQuery({
    queryKey: planBriefsKey(planId),
    queryFn: async () => {
      const res = await axiosInstance.get<PlanBriefsResponse>(
        `/content-calendar/plans/${planId}/briefs`,
      );
      return res.data;
    },
    enabled: !!planId && enabled,
    // The fan-out claims the plan into BRIEFS_GENERATING BEFORE creating the
    // brief rows, so the predicate keys off planStatus as well as row states —
    // a fetch landing in that window used to return zero GENERATING rows and
    // stop the poll for good, freezing the board on "Awaiting brief".
    refetchInterval: (query) => livePoll(live, clampWhenHidden(planBriefsPollMs(query.state.data))),
    refetchIntervalInBackground: !live,
    refetchOnWindowFocus: !live,
  });
  useRefetchRevisionsOnMove((query.data?.briefs ?? []).map(briefStamp).join("|"));
  return query;
}

const ACCEPTABLE_WITH_POST: ProductionBrief["status"][] = ["DRAFT", "IN_REVIEW", "CHANGES_REQUESTED"];

/**
 * The team approving a post approves its brief in the same answer: show the
 * brief accepted at once, in both caches that hold it. Returns the undo, which
 * puts back only this post's brief.
 */
export async function acceptBriefWithPost(
  qc: QueryClient,
  planId: string | null,
  slotId: string,
): Promise<() => void> {
  await Promise.all([
    qc.cancelQueries({ queryKey: slotBriefKey(slotId) }),
    qc.cancelQueries({ queryKey: planBriefsKey(planId) }),
  ]);
  const entry = qc.getQueryData<SlotBriefEntry>(slotBriefKey(slotId));
  const before = qc.getQueryData<PlanBriefsResponse>(planBriefsKey(planId))?.briefs.find((b) => b.slotId === slotId);
  const accept = (brief: ProductionBrief): ProductionBrief =>
    ACCEPTABLE_WITH_POST.includes(brief.status) ? { ...brief, status: "ACCEPTED", acceptanceSource: "TEAM" } : brief;
  const putInPlan = (pick: (brief: ProductionBrief) => ProductionBrief) =>
    qc.setQueryData<PlanBriefsResponse>(planBriefsKey(planId), (prev) =>
      prev ? { ...prev, briefs: prev.briefs.map((b) => (b.slotId === slotId ? pick(b) : b)) } : prev,
    );
  if (entry?.brief) qc.setQueryData<SlotBriefEntry>(slotBriefKey(slotId), { ...entry, brief: accept(entry.brief) });
  putInPlan(accept);
  return () => {
    if (entry) qc.setQueryData(slotBriefKey(slotId), entry);
    if (before) putInPlan(() => before);
  };
}

/** Rail info keyed by slot — what the grid, the pulse and the stage rail need. */
export function briefBySlot(data: PlanBriefsResponse | undefined): Map<string, BriefRailInfo> {
  const map = new Map<string, BriefRailInfo>();
  for (const b of data?.briefs ?? []) {
    map.set(b.slotId, {
      id: b.id,
      slotId: b.slotId,
      status: b.status,
      kind: b.kind,
      version: b.version,
      sentVersion: b.sentVersion,
      updatedAt: b.updatedAt,
    });
  }
  return map;
}

/**
 * One post's brief plus its thread — the workroom panel. `planStatus` matters:
 * `brief: null` during CLIENT_APPROVED/BRIEFS_GENERATING means "the fan-out
 * hasn't created the row yet", which must poll fast, not freeze.
 */
export function useSlotBrief(
  slotId: string | null,
  enabled = true,
  planStatus?: string,
  slotStatus?: string,
) {
  const live = useCalendarLive();
  const query = useQuery({
    queryKey: slotBriefKey(slotId),
    queryFn: async () => {
      const res = await axiosInstance.get<{
        brief: ProductionBrief | null;
        comments: BriefComment[];
      }>(`/content-calendar/plan-slots/${slotId}/brief`);
      return res.data;
    },
    enabled: !!slotId && enabled,
    refetchInterval: (query) =>
      livePoll(live, clampWhenHidden(slotBriefPollMs(query.state.data, planStatus, slotStatus))),
    refetchIntervalInBackground: !live,
    refetchOnWindowFocus: !live,
  });
  useRefetchRevisionsOnMove(query.data?.brief ? briefStamp(query.data.brief) : "");
  return query;
}

export function useBriefRevisions(briefId: string | null, enabled = true) {
  return useQuery({
    queryKey: briefRevisionsKey(briefId),
    queryFn: async () => {
      const res = await axiosInstance.get<{
        briefId: string;
        currentVersion: number;
        sentVersion: number | null;
        revisions: BriefRevisionEntry[];
      }>(`/content-calendar/briefs/${briefId}/revisions`);
      return res.data;
    },
    enabled: !!briefId && enabled,
  });
}

export function useBriefReviewHistory(planId: string | null, enabled = true) {
  const live = useCalendarLive();
  return useQuery({
    queryKey: briefReviewKey(planId),
    queryFn: async () => {
      const res = await axiosInstance.get<{ planStatus: string; rounds: BriefReviewRound[] }>(
        `/content-calendar/plans/${planId}/brief-review`,
      );
      return res.data;
    },
    enabled: !!planId && enabled,
    // An open round's decisions land from the client portal with no action in
    // this tab — without this the round panel freezes mid-round.
    refetchInterval: (query) => livePoll(live, briefReviewPollMs(query.state.data)),
    refetchOnWindowFocus: !live,
  });
}

/**
 * Save a hand-edit.
 *
 * `baseVersion` is not optional and not cosmetic: the server's compare-and-swap
 * rejects a save written against a version somebody else has already replaced,
 * and answers with the current one so this can offer a reload rather than
 * silently clobbering their work.
 */
export function useEditBrief(planId: string | null, slotId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      briefId: string;
      baseVersion: number;
      body: ProductionBriefBody;
      changeNote?: string;
      /** A picture pick: the card filling is the confirmation, no toast. */
      quiet?: boolean;
    }) => {
      const res = await axiosInstance.patch<{ brief: ProductionBrief }>(
        `/content-calendar/briefs/${vars.briefId}`,
        { baseVersion: vars.baseVersion, body: vars.body, changeNote: vars.changeNote },
      );
      return res.data.brief;
    },
    // A read already on its way answers with the version this save replaces;
    // landing after the save, it would paint the old brief back.
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: slotBriefKey(slotId) });
    },
    onSuccess: (brief, vars) => {
      qc.setQueryData<{ brief: ProductionBrief | null; comments: BriefComment[] }>(
        slotBriefKey(slotId),
        (prev) => (prev ? { ...prev, brief } : prev),
      );
      void qc.invalidateQueries({ queryKey: planBriefsKey(planId) });
      void qc.invalidateQueries({ queryKey: briefRevisionsKey(brief.id) });
      if (!vars.quiet) queue.add({ title: "Brief saved", description: `Now on version ${brief.version}.` });
    },
    onError: (err) =>
      queue.add({
        title: "Could not save the brief",
        description: errorMessage(err, "Something went wrong."),
        variant: "error",
      }),
  });
}

export function useAddBriefComment(slotId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { briefId: string; body: string }) => {
      const res = await axiosInstance.post<{ comment: BriefComment }>(
        `/content-calendar/briefs/${vars.briefId}/comments`,
        { body: vars.body },
      );
      return res.data.comment;
    },
    onSuccess: (comment) => {
      qc.setQueryData<{ brief: ProductionBrief | null; comments: BriefComment[] }>(
        slotBriefKey(slotId),
        (prev) => (prev ? { ...prev, comments: [...prev.comments, comment] } : prev),
      );
    },
    onError: (err) =>
      queue.add({
        title: "Could not post that",
        description: errorMessage(err, "Something went wrong."),
        variant: "error",
      }),
  });
}

interface SendBriefsResult {
  plan: CalendarMonthPlan;
  round: { number: number; awaiting: number };
}

/**
 * Put briefs on the client's board. `slotIds` shares exactly those posts'
 * briefs; omitting it shares every ready one — the header button's default.
 *
 * Silent: nobody is emailed. The client sees them next time they open the
 * month in their account; the send dialog's email option is what tells them to.
 */
export function useSendBriefsForReview(planId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slotIds?: string[]) => {
      const res = await axiosInstance.post<SendBriefsResult>(
        `/content-calendar/plans/${planId}/send-briefs`,
        slotIds && slotIds.length > 0 ? { slotIds } : {},
      );
      return res.data;
    },
    // The named posts' briefs read "with the client" on the click; each one
    // rolls back on its own if the send fails.
    onMutate: async (slotIds?: string[]) => {
      const previous = new Map<string, SlotBriefEntry>();
      for (const slotId of slotIds ?? []) {
        await qc.cancelQueries({ queryKey: slotBriefKey(slotId) });
        const entry = qc.getQueryData<SlotBriefEntry>(slotBriefKey(slotId));
        if (!entry?.brief) continue;
        previous.set(slotId, entry);
        qc.setQueryData<SlotBriefEntry>(slotBriefKey(slotId), {
          ...entry,
          brief: { ...entry.brief, status: "IN_REVIEW", sentVersion: entry.brief.version },
        });
      }
      return { previous };
    },
    onSettled: (_result, _err, slotIds) => {
      for (const slotId of slotIds ?? []) void qc.invalidateQueries({ queryKey: slotBriefKey(slotId) });
    },
    onSuccess: (result) => {
      reconcilePlan(qc, planId);
      void qc.invalidateQueries({ queryKey: planBriefsKey(planId) });
      void qc.invalidateQueries({ queryKey: briefReviewKey(planId) });
      const n = result.round.awaiting;
      queue.add({
        title: `${n} brief${n === 1 ? "" : "s"} shared with the client`,
        description:
          "On their Content review page now — no email sent.",
      });
    },
    onError: (err, _slotIds, context) => {
      for (const [slotId, entry] of context?.previous ?? []) {
        qc.setQueryData(slotBriefKey(slotId), entry);
      }
      queue.add({
        title: "Could not share the briefs",
        description: errorMessage(err, "Something went wrong."),
        variant: "error",
      });
    },
  });
}

/**
 * Accept a brief WITHOUT the client — the brief-stage twin of the team approval.
 *
 * It accepts the brief's CURRENT version, not the one last sent: a hand-edit
 * made after the send is exactly what the team is signing off here. From
 * IN_REVIEW the server detaches the brief from the open round first, and loses
 * to a client who answered inside that window (409).
 *
 * ACCEPTED is what unlocks media upload and scheduling, so the cache moves on
 * the click — the "Ready to post" tab appearing IS the confirmation.
 */
export function useTeamAcceptBrief(planId: string | null, slotId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.post<{ brief: ProductionBrief }>(
        `/content-calendar/plan-slots/${slotId}/brief/accept`,
      );
      return res.data.brief;
    },
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: slotBriefKey(slotId) });
      const previous = qc.getQueryData<SlotBriefEntry>(slotBriefKey(slotId));
      qc.setQueryData<SlotBriefEntry>(slotBriefKey(slotId), (prev) =>
        prev?.brief
          ? { ...prev, brief: { ...prev.brief, status: "ACCEPTED", acceptanceSource: "TEAM" } }
          : prev,
      );
      return { previous };
    },
    onSuccess: (brief) => {
      qc.setQueryData<SlotBriefEntry>(slotBriefKey(slotId), (prev) =>
        prev ? { ...prev, brief } : prev,
      );
      void qc.invalidateQueries({ queryKey: planBriefsKey(planId) });
      reconcilePlan(qc, planId);
      queue.add({
        title: "Approved without the client",
        description: "The post is in production. Its files come in next.",
        variant: "success",
      });
    },
    onError: (err, _vars, context) => {
      if (context?.previous) qc.setQueryData(slotBriefKey(slotId), context.previous);
      queue.add({
        title: "Could not approve the brief",
        description: errorMessage(err, "Something went wrong."),
        variant: "error",
      });
      void qc.invalidateQueries({ queryKey: slotBriefKey(slotId) });
    },
  });
}

/** Pull an open round back when a client has gone quiet. */
export function useRecallBriefRound(planId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.post<{ plan: CalendarMonthPlan }>(
        `/content-calendar/plans/${planId}/recall-briefs`,
      );
      return res.data.plan;
    },
    onSuccess: () => {
      reconcilePlan(qc, planId);
      void qc.invalidateQueries({ queryKey: planBriefsKey(planId) });
      void qc.invalidateQueries({ queryKey: briefReviewKey(planId) });
      queue.add({
        title: "Review pulled back",
        description: "The briefs are yours again. The client's link no longer accepts decisions.",
      });
    },
    onError: (err) =>
      queue.add({
        title: "Could not pull the review back",
        description: errorMessage(err, "Something went wrong."),
        variant: "error",
      }),
  });
}

/** Take ONE brief off the client's board. */
export function useRecallSlotBrief(planId: string | null, slotId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.post<{ plan: CalendarMonthPlan }>(
        `/content-calendar/plans/${planId}/slots/${slotId}/recall-brief`,
      );
      return res.data.plan;
    },
    onSuccess: () => {
      reconcilePlan(qc, planId);
      void qc.invalidateQueries({ queryKey: planBriefsKey(planId) });
      void qc.invalidateQueries({ queryKey: slotBriefKey(slotId) });
      void qc.invalidateQueries({ queryKey: briefReviewKey(planId) });
      queue.add({
        title: "Brief pulled back",
        description:
          "It's off the client's page and yours again — they were never told, so nothing to explain.",
      });
    },
    onError: (err) =>
      queue.add({
        title: "Could not pull that brief back",
        description: errorMessage(err, "Something went wrong."),
        variant: "error",
      }),
  });
}

/**
 * Ask the agent to write a FAILED brief again.
 *
 * The one place the model gets a second go at a brief, and only from FAILED — a
 * brief in that state provably has no body, so there is nothing to overwrite.
 * Once a brief has a draft it belongs to a person; `useEditBrief` is the only way
 * its content changes from then on.
 */
export function useRetryBriefGeneration(planId: string | null, slotId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (briefId: string) => {
      const res = await axiosInstance.post<{ brief: ProductionBrief }>(
        `/content-calendar/briefs/${briefId}/retry`,
      );
      return res.data.brief;
    },
    onSuccess: (brief) => {
      // The row is already GENERATING when this returns; writing it straight in
      // starts the poll (useSlotBrief keys its interval off the status) and
      // swaps the failure banner for the skeleton without a round trip.
      qc.setQueryData<{ brief: ProductionBrief | null; comments: BriefComment[] }>(
        slotBriefKey(slotId),
        (prev) => (prev ? { ...prev, brief } : prev),
      );
      void qc.invalidateQueries({ queryKey: planBriefsKey(planId) });
      queue.add({
        title: "Writing the brief again",
        description: "A fresh attempt is running — it lands on the post in a minute.",
        variant: "info",
      });
    },
    onError: (err) =>
      queue.add({
        title: "Could not retry the brief",
        description: errorMessage(err, "Something went wrong."),
        variant: "error",
      }),
  });
}

/**
 * Condemn one brief a restart stranded mid-write, so it can be retried or
 * hand-written.
 *
 * FAILED is not a dead end — it is the hand-editable state the brief stage is
 * designed around (saving a body promotes it to DRAFT), and the one state
 * `useRetryBriefGeneration` may point the model at again.
 */
export function useForceFailBrief(planId: string | null, slotId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (briefId: string) => {
      const res = await axiosInstance.post<{ brief: ProductionBrief }>(
        `/content-calendar/briefs/${briefId}/force-fail`,
      );
      return res.data.brief;
    },
    onSuccess: (brief) => {
      qc.setQueryData<{ brief: ProductionBrief | null; comments: BriefComment[] }>(
        slotBriefKey(slotId),
        (prev) => (prev ? { ...prev, brief } : prev),
      );
      void qc.invalidateQueries({ queryKey: planBriefsKey(planId) });
      queue.add({
        title: "Brief marked as failed",
        description: "Write it by hand — the agent does not retry a creative brief.",
      });
    },
    onError: (err) =>
      queue.add({
        title: "Could not mark the brief as failed",
        description: errorMessage(err, "Something went wrong."),
        variant: "error",
      }),
  });
}

/**
 * The escape hatch when every brief in a month failed to generate.
 *
 * The month stays at BRIEFS_GENERATING in that case rather than advancing an
 * empty month, so this is the operator saying "fine, I'll write them myself".
 */
// `useUnstickBriefs` is gone with the month-level brief fan-out: there is no
// BRIEFS_GENERATING month to be stuck in. A single dead brief run is condemned
// from its own card (force-fail) and then written by hand or retried.
